// Step-by-step questionnaire driven entirely by the event type's wizardSteps.
// Stateless about persistence — emits `details` upward via onChange; the editor
// owns saving. This keeps the wizard reusable and easy to test.

import { useEffect, useMemo, useRef, useState } from "react";
import { Field } from "./fields.jsx";

const stepPreviewPage = (step) => step.fields.find((field) => field.previewPage)?.previewPage || "cover";

export default function Wizard({ eventType, details, onChange, slug, onFocusPage, onStepChange }) {
  const steps = eventType.wizardSteps;
  const [step, setStep] = useState(0);
  const current = steps[step];
  const pillsRef = useRef(null);
  const activePillRef = useRef(null);
  const visibleFields = useMemo(
    () => current.fields.filter((field) => !field.showWhen || field.showWhen(details)),
    [current, details]
  );

  const requiredMissing = useMemo(() => {
    return visibleFields.some((f) => f.required && !details[f.key]);
  }, [visibleFields, details]);

  function setField(field, val) {
    const next = { ...details, [field.key]: val };
    if (field.kind === "toggle" && val && field.defaultPatch) {
      for (const [patchKey, patchValue] of Object.entries(field.defaultPatch)) {
        const existing = next[patchKey];
        if (
          existing === undefined ||
          existing === null ||
          existing === "" ||
          (Array.isArray(existing) && existing.length === 0)
        ) {
          next[patchKey] = patchValue;
        }
      }
    }
    onChange(next);
  }

  useEffect(() => {
    onFocusPage?.(stepPreviewPage(current));
  }, [current, onFocusPage]);

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Scroll active pill into view whenever step changes
  useEffect(() => {
    const pill = activePillRef.current;
    const container = pillsRef.current;
    if (!pill || !container) return;
    const pillLeft = pill.offsetLeft;
    const pillRight = pillLeft + pill.offsetWidth;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.offsetWidth;
    if (pillLeft < viewLeft) {
      container.scrollTo({ left: pillLeft - 8, behavior: "smooth" });
    } else if (pillRight > viewRight) {
      container.scrollTo({ left: pillRight - container.offsetWidth + 8, behavior: "smooth" });
    }
  }, [step]);

  return (
    <div className="stack">
      {/* Progress bar */}
      <div className="row" style={{ gap: 6 }}>
        {steps.map((s, i) => (
          <div
            key={s.id}
            style={{
              height: 4, flex: 1, borderRadius: 4,
              background: i <= step ? "var(--app-accent)" : "var(--app-border)",
              transition: "background 0.2s",
            }}
          />
        ))}
      </div>

      {/* Horizontally-scrollable step pills */}
      <div className="wizard-step-pills" ref={pillsRef}>
        {steps.map((s, i) => (
          <button
            key={s.id}
            ref={i === step ? activePillRef : null}
            type="button"
            className={`wizard-step-pill${i === step ? " active" : ""}${i < step ? " done" : ""}`}
            onClick={() => setStep(i)}
          >
            <span className="wizard-step-pill-num">{i + 1}</span>
            {s.title}
          </button>
        ))}
      </div>

      {/* Back / Next nav */}
      <div className="wizard-nav">
        <button className="btn-ghost btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
          ← Back
        </button>
        <h2 style={{ margin: 0, flex: 1, fontSize: "1.1rem" }}>{current.title}</h2>
        {step < steps.length - 1 ? (
          <button className="btn" disabled={requiredMissing} onClick={() => setStep((s) => s + 1)}>
            Next →
          </button>
        ) : (
          <span className="muted" style={{ fontSize: ".85rem", whiteSpace: "nowrap" }}>All done ✓</span>
        )}
      </div>

      <div className="stack">
        {visibleFields.map((f) => (
          <Field
            key={f.key}
            field={f}
            value={details[f.key]}
            details={details}
            onChange={(v) => setField(f, v)}
            onFocus={() => onFocusPage?.(f.previewPage || stepPreviewPage(current))}
            slug={slug}
          />
        ))}
      </div>
    </div>
  );
}
